import ValueFormatterFactory from "@/utils/UIConstants/ValueFormatterFactory.ts";
import AbsEditViewValueFormatter from "@/utils/UIConstants/AGGridEditViewValueFormatters/AbsEditViewValueFormatter.ts";
import {computed, ref, Ref, watchEffect} from "vue";
import NamaCommon from "@/utils/common/NamaCommon.ts";
import {useLoadingStore} from "@/stores/LoadingStore.ts";
import {WidgetsPresenter} from "@/utils/presenters/interfaces.ts";
import CommonFieldIds from "@/utils/constants/CommonFieldIds.ts";
import ClientCache from "@/utils/login/ClientCache.ts";
import type {ICellEditorParams, ICellRendererParams} from "ag-grid-community";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import NumberUtils from "@/utils/common/NumberUtils.ts";
import EditScreenFieldLayout from "@/utils/constants/EditScreenFieldLayout.ts";
import FieldMetaData = namaTypes.FieldMetaData;
import ListOfString = namaTypes.ListOfString;
import CmnGUIPostAction = namaTypes.CmnGUIPostAction;
import FieldStyle = namaTypes.FieldStyle;
import DTOFieldSpecs = namaTypes.DTOFieldSpecs;
import EditScreenField = namaTypes.EditScreenField;

export default class FieldWidgetUtil {
  formatterInstance: AbsEditViewValueFormatter;
  fieldMetaData: FieldMetaData;
  presenter: WidgetsPresenter;
  agCellParams?: ICellRendererParams | ICellEditorParams;
  static focusedField: FieldWidgetUtil | null = null;
  static columnSetterCB: ((columnId: string, row: number, value: any) => void) | null = null;
  static EditScreenGridSavers: (() => void)[] = [];
  /**
   * Value after first edit, or after calling post action.
   * It is used mainly to check if the user changed the value after focusing the widget or not
   */
  inputFieldValueRef: Ref<any> = ref();
  isSuggestionProviderField: boolean;
  isFieldFocused: Ref<boolean> = ref(false)

  constructor(presenter: WidgetsPresenter, fieldMetaData: FieldMetaData, doNotInitializeInputValue?: boolean) {
    this.presenter = presenter;
    this.fieldMetaData = fieldMetaData;
    this.formatterInstance = ValueFormatterFactory.fromFieldType(fieldMetaData.fieldType);
    if (!doNotInitializeInputValue) {
      watchEffect(() => {
        this.inputFieldValueRef.value = this.formattedValue().value;
      });
    }
    this.isSuggestionProviderField = this.presenter.doesFieldHasSuggestionProvider(fieldMetaData);
  }

  displayTooltip() {

  }

  getLineNumberDelegate():any {
    return;
  }

  handlesTooltips(): boolean {
    return false;
  }

  isFieldRequired() {
    return this.fieldMetaData.required === true;
  }

  isFieldDisabled(forceEnableField: boolean | undefined) {
    if (forceEnableField)
      return false
    if (this.fieldId() === "barcode")
      return false;
    if (this.fieldMetaData && (this.fieldMetaData.systemField || (this.fieldMetaData.guiField && !this.fieldMetaData.enabled)))
      return true;
    return this.presenter.isFieldDisabled(this.fieldId())
  }
  fieldId(): string {
    return this.fieldMetaData.fieldId!;
  }

  formattedValue() {
    const fieldSpecs = ObjectChecker.getFirstNotEmptyObj(this.createSpecs(), this.fieldMetaData.fieldSpecs);
    return computed(() => this.formatterInstance.format(fieldSpecs, this.fetchFieldValue()));
  }

  createSpecs(): namaTypes.DTOFieldSpecs | null | undefined {
    return {
      decimalPlaces: NumberUtils.calcFractionalDecimalPlaces(this.fieldMetaData, this.presenter.fetchRecord()),
      format: ClientCache.getFieldDisplayMask(this.presenter.entityType, this.fieldMetaData.fieldId)
    } as DTOFieldSpecs;
  }

  fetchFieldValue(): any {
    return NamaCommon.getFieldValue(this.presenter.fetchRecord(), this.fieldMetaData.fieldId!);
  }

  onPartialUserInput = (value: any) => {
    this.inputFieldValueRef.value = value;
  }

  onBlur = async () => {
    await this.onUserInputFinished();
    if (FieldWidgetUtil.focusedField === this)
      FieldWidgetUtil.focusedField = null;
    this.isFieldFocused.value = false;
  }

  onFocus = async () => {
    FieldWidgetUtil.focusedField = this;
    this.isFieldFocused.value = true;
  }

  saveChanges = async () => {
    await this.onUserInputFinished();
  }
  onUserInputFinished = async () => {
    let inputFieldValue = this.inputFieldValueRef.value;
    this.onPartialUserInput(inputFieldValue);
    let originalValue = this.fetchFieldValue();
    let parsedValue = this.formatterInstance.parse(this.fieldMetaData.fieldSpecs, inputFieldValue, originalValue);
    await this.updateValueAndCallPostAction(parsedValue, originalValue);
  }

  protected async updateValueAndCallPostAction(parsedValue: any, originalValue: any) {
    NamaCommon.setFieldValue(this.presenter.fetchRecord(), this.fieldId(), parsedValue, this.agCellParams?.node?.rowIndex);
    if (originalValue !== parsedValue) {
      const loadingStore = useLoadingStore();
      loadingStore.startLoading();
      if (this.presenter.fetchDispatcher())
        await this.presenter.fetchDispatcher().validate(this.fieldId())
      loadingStore.stopLoading();
    }
  }

  isCodeFieldOrExtraCodeField() {
    const isCodeField = this.fieldMetaData.id === CommonFieldIds.CODE;
    return isCodeField || this.isExtraCodeField();
  }

  isExtraCodeField() {
    let result = this.fieldMetaData.id === CommonFieldIds.ALT_CODE;
    if (result)
      return true;
    let data = ClientCache.getCustomGUISettings()?.tooltipsAndReportsAndRevise?.entitiesExtraCodes;
    if (!data || !data[this.presenter.entityType])
      return false
    let extraCodeFieldIds = (data[this.presenter.entityType] as ListOfString).list ?? [];
    return extraCodeFieldIds.includes(this.fieldMetaData.id!);
  }

  createContextFieldsIds() {
    let allContextFieldsIds: string[] = [];
    let fieldInfo = this.fieldMetaData.fieldInformation;
    if (fieldInfo && fieldInfo.suggestionProviderContextFields?.length)
      allContextFieldsIds.push(...fieldInfo.suggestionProviderContextFields)
    let suggestionProviders = ClientCache.getCustomGUISettings()?.tooltipsAndReportsAndRevise?.suggestionProviders;
    if (suggestionProviders && suggestionProviders[this.presenter.entityType + "." + this.fieldId()]) {
      let suggestionProvider = suggestionProviders[this.presenter.entityType + "." + this.fieldId()] as CmnGUIPostAction;
      allContextFieldsIds.push(...(suggestionProvider.contextFields ?? []))
    }
    if (fieldInfo && fieldInfo.dynamicSearchContextFields?.length)
      allContextFieldsIds.push(...fieldInfo.dynamicSearchContextFields)
    return allContextFieldsIds;
  }

  static addEditScreenGridSaver(saveEditingCell: () => void) {
    this.EditScreenGridSavers.push(saveEditingCell);
  }

  static removeEditScreenGridSaver(saveEditingCell: () => void) {
    const index = this.EditScreenGridSavers.indexOf(saveEditingCell, 0);
    if (index > -1) {
      this.EditScreenGridSavers.splice(index, 1);
    }
  }

  static async saveEditingFieldsAndGrids() {
    if (this.focusedField)
      await this.focusedField.saveChanges();
    for (let editScreenGridSaver of this.EditScreenGridSavers) {
      if (editScreenGridSaver)
        editScreenGridSaver();
    }
    await useLoadingStore().awaitLoadingFinish(50);
  }

  getFieldStyle(): FieldStyle {
    const fieldStyle = ClientCache.getFieldStyle(this.presenter.entityType, this.fieldId());
    if (!fieldStyle)
      return {};
    return fieldStyle;
  }

 static isSpanned(questionField: Pick<EditScreenField, "layout">): boolean {
   const layout = questionField?.layout;
   return layout === EditScreenFieldLayout.spanned || layout === EditScreenFieldLayout.spanned2
  }
}
